"use client";

import { ToggleGroup } from "@cmp-src/primitives/toggle-group";
import {
  AuthenticatedUser,
  WorkplaceRequestComment,
  WorkplaceRequestCommentAttachmentInput,
  WorkplaceRequestEvent,
  WorkplaceRequestEventUser
} from "@gettactic/api";
import { CommentsSection } from "../RequestDetailsModal/CommentsSection";
import { classes } from "../RequestDetailsModal/utils";
import { RequestEventsList } from "../RequestEventsList";

type ActivityTab = "comments" | "activity";

interface CommentsActivitySectionProps {
  activityTab: ActivityTab;
  onActivityTabChange: (tab: ActivityTab) => void;
  comments: WorkplaceRequestComment[];
  commentsLoading: boolean;
  authenticatedUser: Pick<AuthenticatedUser, "user">;
  editingCommentId: string | null;
  editingCommentText: string;
  onStartEditComment: (comment: WorkplaceRequestComment) => void;
  onCancelEditComment: () => void;
  onEditComment: (
    commentId: string,
    attachments?: WorkplaceRequestCommentAttachmentInput[]
  ) => void;
  onDeleteComment: (commentId: string) => void;
  onEditingCommentTextChange: (value: string) => void;
  updateCommentLoading: boolean;
  events: WorkplaceRequestEvent[];
  eventUsers: Record<string, WorkplaceRequestEventUser>;
  eventsLoading: boolean;
}

/**
 * Combined Comments / Activity section with a shared {@link ToggleGroup} between the
 * two. Defaults to Comments (the composer lives in the modal footer, not here).
 */
export const CommentsActivitySection = ({
  activityTab,
  onActivityTabChange,
  comments,
  commentsLoading,
  authenticatedUser,
  editingCommentId,
  editingCommentText,
  onStartEditComment,
  onCancelEditComment,
  onEditComment,
  onDeleteComment,
  onEditingCommentTextChange,
  updateCommentLoading,
  events,
  eventUsers,
  eventsLoading
}: CommentsActivitySectionProps) => (
  <div className="-mx-8 mt-8 px-8 pt-6 max-sm:-mx-4 max-sm:px-4">
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className={classes.sectionHeading}>Comments</h3>
      <ToggleGroup
        value={activityTab}
        onValueChange={(value) => onActivityTabChange(value as ActivityTab)}
        items={[
          { value: "activity", label: "Activity" },
          { value: "comments", label: "Comments" }
        ]}
      />
    </div>

    {activityTab === "comments" ? (
      <CommentsSection
        embedded
        comments={comments}
        commentsLoading={commentsLoading}
        authenticatedUser={authenticatedUser}
        editingCommentId={editingCommentId}
        editingCommentText={editingCommentText}
        onStartEditComment={onStartEditComment}
        onCancelEditComment={onCancelEditComment}
        onEditComment={onEditComment}
        onDeleteComment={onDeleteComment}
        updateCommentLoading={updateCommentLoading}
        onEditingCommentTextChange={onEditingCommentTextChange}
      />
    ) : (
      <RequestEventsList
        events={events}
        users={eventUsers}
        isLoading={eventsLoading}
      />
    )}
  </div>
);
